/**
 * Auto-Renewal Settings Component
 * Allows users to configure automatic package renewal
 */

import * as React from "react"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Checkbox } from "@/components/ui/checkbox"
import { Loader2, AlertCircle, CheckCircle, Info, CreditCard, QrCode } from "lucide-react"
import { useAutoRenewalSettings, useUpdateAutoRenewal, useAutoRenewalPreview } from "@/lib/api/auto-renewal"
import type { AutoRenewalMethod, NotificationChannel } from "@/src/types/auto-renewal"
import { toast } from "sonner"

export function AutoRenewalSettings() {
  const { data: settingsData, isLoading: isLoadingSettings } = useAutoRenewalSettings()
  const updateMutation = useUpdateAutoRenewal()

  // Local state
  const [enabled, setEnabled] = React.useState(false)
  const [paymentMethod, setPaymentMethod] = React.useState<AutoRenewalMethod>('qr_code')
  const [notifyVia, setNotifyVia] = React.useState<NotificationChannel[]>(['email'])
  const [notifyBeforeDays, setNotifyBeforeDays] = React.useState<number[]>([7, 3, 1])
  
  // Preview data based on selected method
  const { data: previewData } = useAutoRenewalPreview(paymentMethod)

  // Sync with API data
  React.useEffect(() => {
    if (settingsData?.data) {
      setEnabled(settingsData.data.auto_renewal_enabled)
      if (settingsData.data.auto_renewal_method) {
        setPaymentMethod(settingsData.data.auto_renewal_method)
      }
      if (settingsData.data.auto_renewal_settings) {
        const settings = settingsData.data.auto_renewal_settings
        setNotifyVia(settings.notify_via || ['email'])
        setNotifyBeforeDays(settings.notify_before_days || [7, 3, 1])
      }
    }
  }, [settingsData])

  const handleSave = async () => {
    try {
      await updateMutation.mutateAsync({
        enabled,
        method: paymentMethod,
        settings: {
          notify_before_days: notifyBeforeDays,
          notify_via: notifyVia,
          auto_renew_with_credits: paymentMethod === 'credit_balance'
        }
      })
      toast.success('บันทึกการตั้งค่าสำเร็จ')
    } catch (error: any) {
      toast.error(`เกิดข้อผิดพลาด: ${error.message}`)
    }
  }

  if (isLoadingSettings) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      </Card>
    )
  }

  const currentPackage = settingsData?.data?.current_package
  const preview = previewData?.data

  
  // Helper to toggle notification channel
  const toggleNotificationChannel = (channel: NotificationChannel) => {
    setNotifyVia(prev => 
      prev.includes(channel) 
        ? prev.filter(c => c !== channel)
        : [...prev, channel]
    )
  }
  return (
    <Card className="p-6">
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h3 className="text-lg font-semibold mb-1">ต่ออายุแพ็กเกจอัตโนมัติ</h3>
          <p className="text-sm text-gray-600">
            ตั้งค่าการต่ออายุแพ็กเกจอัตโนมัติเมื่อใกล้หมดอายุ
          </p>
        </div>

        {/* Current Package Info */}
        {currentPackage && (
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="space-y-1">
                <p className="text-sm font-semibold text-blue-900">
                  แพ็กเกจปัจจุบัน: {currentPackage.name}
                </p>
                <p className="text-sm text-blue-800">
                  ราคา: ฿{parseFloat(currentPackage.monthly_price?.toString() || '0').toLocaleString()}/เดือน
                </p>
                {currentPackage.expires_at && (
                  <p className="text-sm text-blue-800">
                    หมดอายุ: {new Date(currentPackage.expires_at).toLocaleDateString('th-TH', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })} ({currentPackage.days_remaining} วัน)
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Enable/Disable Toggle */}
        <div className="flex items-center justify-between p-4 border rounded-lg">
          <div>
            <Label htmlFor="auto-renewal-toggle" className="text-base font-semibold">
              เปิดใช้งานการต่ออายุอัตโนมัติ
            </Label>
            <p className="text-sm text-gray-600 mt-1">
              ระบบจะต่ออายุแพ็กเกจให้อัตโนมัติก่อนหมดอายุ
            </p>
          </div>
          <Switch
            id="auto-renewal-toggle"
            checked={enabled}
            onCheckedChange={setEnabled}
          />
        </div>

        {/* Settings (only show when enabled) */}
        {enabled && (
          <>
            {/* Payment Method Selection */}
            <div className="space-y-4">
              <Label className="text-base font-semibold">วิธีการชำระเงิน</Label>
              
              <RadioGroup value={paymentMethod} onValueChange={(value: any) => setPaymentMethod(value)}>
                {/* QR Code Option */}
                <div className="flex items-start space-x-3 p-4 border rounded-lg hover:bg-gray-50 cursor-pointer">
                  <RadioGroupItem value="qr_code" id="qr_code" className="mt-1" />
                  <div className="flex-1">
                    <Label htmlFor="qr_code" className="flex items-center gap-2 font-semibold cursor-pointer">
                      <QrCode className="w-5 h-5 text-orange-600" />
                      QR Code (ชำระด้วยตนเอง)
                    </Label>
                    <p className="text-sm text-gray-600 mt-1">
                      ระบบจะส่ง QR Code ให้คุณล่วงหน้า 7 วันก่อนหมดอายุ คุณต้องสแกนและชำระเงินเอง
                    </p>
                    <div className="mt-2 p-2 bg-amber-50 rounded border border-amber-200">
                      <p className="text-xs text-amber-800">
                        ⚠️ หากไม่ชำระภายในเวลาที่กำหนด แพ็กเกจจะหมดอายุ
                      </p>
                    </div>
                  </div>
                </div>

                {/* Credit Balance Option */}
                <div className="flex items-start space-x-3 p-4 border rounded-lg hover:bg-gray-50 cursor-pointer">
                  <RadioGroupItem value="credit_balance" id="credit_balance" className="mt-1" />
                  <div className="flex-1">
                    <Label htmlFor="credit_balance" className="flex items-center gap-2 font-semibold cursor-pointer">
                      <CreditCard className="w-5 h-5 text-green-600" />
                      หักเครดิตอัตโนมัติ
                    </Label>
                    <p className="text-sm text-gray-600 mt-1">
                      ระบบจะหักเครดิตจากบัญชีของคุณอัตโนมัติเมื่อใกล้หมดอายุ
                    </p>
                    
                    {/* Credit Balance Preview */}
                    {preview && (
                      <div className="mt-3 space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">เครดิตคงเหลือ:</span>
                          <span className="font-semibold">{preview.credit_balance.current.toLocaleString()} เครดิต</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">ค่าต่ออายุ:</span>
                          <span className="font-semibold">฿{preview.cost.amount.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between text-sm border-t pt-2">
                          <span className="text-gray-600">คงเหลือหลังต่ออายุ:</span>
                          <span className={`font-semibold ${preview.credit_balance.is_sufficient ? 'text-green-600' : 'text-red-600'}`}>
                            {preview.credit_balance.after_renewal.toLocaleString()} เครดิต
                          </span>
                        </div>
                      </div>
                    )}

                    {preview && !preview.credit_balance.is_sufficient && (
                      <div className="mt-2 p-2 bg-red-50 rounded border border-red-200">
                        <p className="text-xs text-red-800">
                          ⚠️ เครดิตไม่เพียงพอสำหรับการต่ออายุ (ขาดอีก {(preview.credit_balance.required - preview.credit_balance.current).toLocaleString()} เครดิต)
                        </p>
                      </div>
                    )}

                    {preview && preview.credit_balance.is_sufficient && (
                      <div className="mt-2 p-2 bg-green-50 rounded border border-green-200">
                        <p className="text-xs text-green-800 flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" />
                          เครดิตเพียงพอสำหรับการต่ออายุ
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </RadioGroup>
            </div>

            {/* Notification Preferences */}
            <div className="space-y-4">
              <Label className="text-base font-semibold">การแจ้งเตือน</Label>
              
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <Checkbox
                    id="notify-email"
                    checked={notifyVia.includes('email')}
                    onCheckedChange={() => toggleNotificationChannel('email')}
                  />
                  <Label htmlFor="notify-email" className="text-sm cursor-pointer">
                    แจ้งเตือนทาง Email
                  </Label>
                </div>

                <div className="flex items-center space-x-3">
                  <Checkbox
                    id="notify-line"
                    checked={notifyVia.includes('line_oa')}
                    onCheckedChange={() => toggleNotificationChannel('line_oa')}
                  />
                  <Label htmlFor="notify-line" className="text-sm cursor-pointer">
                    แจ้งเตือนทาง LINE OA (เร็วๆ นี้)
                  </Label>
                </div>

                <div className="pl-6 space-y-2">
                  <Label className="text-sm text-gray-600">
                    แจ้งเตือนล่วงหน้า:
                  </Label>
                  <div className="flex flex-wrap gap-2">
                    {[1, 3, 7, 14, 30].map(days => (
                      <label key={days} className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={notifyBeforeDays.includes(days)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setNotifyBeforeDays([...notifyBeforeDays, days].sort((a, b) => b - a))
                            } else {
                              setNotifyBeforeDays(notifyBeforeDays.filter(d => d !== days))
                            }
                          }}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm">{days} วัน</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Important Notes */}
            <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="space-y-2 text-sm text-amber-900">
                  <p className="font-semibold">ข้อควรทราบ:</p>
                  <ul className="list-disc list-inside space-y-1 text-amber-800">
                    <li>การต่ออายุจะเกิดขึ้นอัตโนมัติก่อนแพ็กเกจหมดอายุ 1 วัน</li>
                    <li>หากเครดิตไม่พียงพอ ระบบจะส่ง QR Code ให้ชำระแทน</li>
                    <li>คุณสามารถยกเลิกการต่ออายุอัตโนมัติได้ทุกเมื่อ</li>
                    <li>แพ็กเกจที่ต่ออายุจะมีระยะเวลา 30 วันนับจากวันหมดอายุ</li>
                  </ul>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Save Button */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button
            onClick={handleSave}
            disabled={updateMutation.isPending}
            className="px-8"
          >
            {updateMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                กำลังบันทึก...
              </>
            ) : (
              'บันทึกการตั้งค่า'
            )}
          </Button>
        </div>
      </div>
    </Card>
  )
}
